// Box — single-row renderer for a country-return box.
// Extracted from CountryReturn.jsx in the Phase-3 refactor.
// v0.4.89 (sign-off 2026-06-20): cards were too dense — every
// box rendered the English label, the computed_from formula,
// the explainer, AND the local label as four stacked lines.
// Operators asked for "label + value first, details behind an
// ⓘ icon". Anything secondary now lives in the InfoCircle
// popover only.
import React from "react";
import { TypedInEditor } from "./TypedInEditor";
import { InfoCircle } from "../InfoCircle";
import { useTranslation } from "react-i18next";
import { isFigureToCopy, isZeroFigure } from "./copyFigure";
import { boxEntry, boxStatus, boxTitle, englishInInfo } from "./boxView";

// Who filled a box, when it was the app rather than the operator. The
// override's `by` is "engine.<source>" (several joined by "+"); anything
// else is a person.
export const filledByApp = (by, tr = (_k, o) => o.defaultValue) => {
  const v = String(by || "");
  if (!v.startsWith("engine.")) return null;
  if (v.includes("engine.purchases")) return tr("ui.country_return.box.filled_purchases", { defaultValue: "Filled from your purchase answers" });
  if (v.includes("carry_forward")) return tr("ui.country_return.box.filled_carry", { defaultValue: "Carried from your last filed period" });
  return tr("ui.country_return.box.filled_app", { defaultValue: "Filled by the app" });
};

export const Box = ({
  box, calcId, country, onSaved, periodStart, clientCadence,
  moduleIndex, routeDestinationOffenders, highlighted, calcFrozen, calcSignedOff,
  entryScale = 1, entryUnit = null, required = false,
  labelLanguage, noteShownOnce = false,
}) => {
  const { t } = useTranslation();
  const ov = box.override;
  // Operator check 2026-10-16: a box the app filled (from purchase answers,
  // or carried from the last filed period) said "You enter the value" and
  // "✓ Value entered by you · engine.purchases", so it read as a manual
  // entry still owed. Say who actually filled it, in plain words.
  const autoFill = filledByApp(ov?.by, t);
  // A2-638 — one title (the form's own words), one status, the English
  // behind the (i) only on a form not in English: decided in boxView.js.
  const { ref: boxRef, label: title } = boxTitle(box);
  const entry = boxEntry(box, { autoFill: !!autoFill, required });
  const english = englishInInfo(box, labelLanguage);
  const statusKey = boxStatus(box, entry, entryScale);
  // a note several boxes share is shown once on the page, not under each
  const note = noteShownOnce ? null : box.out_of_scope_reason;
  const explainer = box.explainer && box.explainer !== box.out_of_scope_reason ? box.explainer : null;
  // A2-478 (operator 2026-10-21) — the return is copied by hand onto the
  // authority's form, so the card says what to do with it THERE: copy it,
  // check the form's own figure, or leave it (not on the form). A box still
  // owed in the app stays loud whatever its role. Older payloads carry no
  // role and keep the A2-476 cards.
  const portal = box.portal || "";
  const PORTAL_CARD = {
    enter: "border-2 border-indigo-400 dark:border-indigo-500 bg-indigo-50/70 dark:bg-indigo-950/30",
    // a panel, not the page colour (a nested block in it reads as a hole)
    calculated: "border surface-panel",
    prefilled: "border surface-panel",
    not_on_form: "border border-dashed border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900",
  };
  const ENTRY_CARD = {
    required: "border-2 border-amber-500 ring-2 ring-amber-400/60 bg-amber-50 dark:bg-amber-950/40",
    todo: "border border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20",
    done: "border border-emerald-300 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/20",
    app: "border border-emerald-300 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/20",
  };
  // A box the operator still has to fill in keeps its amber card unless it is
  // itself a box to copy: an app-side input "not on the form" still feeds a
  // copied box, and a dashed "do not copy" card would read as "ignore".
  // Operator 2026-10-21 ("inconsistent"): a box to copy that is 0 was
  // highlighted like one with a figure while the bar counted only figures.
  // Only a figure to copy is highlighted; a 0 says so; a box owed here first
  // says "fill in here first".
  const zero = !box.error && isZeroFigure(box, entryScale);
  const copyFigure = isFigureToCopy(box, entryScale);
  const card = entry === "required" || (entry === "todo" && portal !== "enter") ? ENTRY_CARD[entry]
    : portal === "enter" && zero ? "border surface-panel"
    : PORTAL_CARD[portal] || ENTRY_CARD[entry] || "border bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-700";
  const STATUS_STYLE = {
    portal_enter: "bg-indigo-600 border-indigo-600 text-white",
    portal_enter_zero: "border-indigo-300 dark:border-indigo-700 text-indigo-800 dark:text-indigo-200",
    portal_enter_optional: "border-amber-400 text-amber-800 dark:text-amber-200",
    portal_enter_first: "border-amber-500 text-amber-800 dark:text-amber-200",
    kind_typed_required: "border-amber-500 text-amber-800 dark:text-amber-200",
    portal_input: "border-amber-500 text-amber-800 dark:text-amber-200",
    kind_optional: "border-amber-400 text-amber-800 dark:text-amber-200",
    portal_not_on_form: "border-dashed border-slate-400 text-slate-600 dark:text-slate-300",
  };
  const STATUS_TEXT = {
    portal_enter: t("ui.country_return.box.portal_enter", "Copy to the form"),
    portal_enter_zero: t("ui.country_return.box.portal_enter_zero", "0 — nothing to copy unless the form asks for a 0"),
    portal_enter_first: t("ui.country_return.box.portal_enter_first", "Fill in here first, then copy to the form"),
    portal_enter_optional: t("ui.country_return.box.portal_enter_optional", "Only if it applies — fill in here, then copy to the form"),
    portal_calculated: t("ui.country_return.box.portal_calculated", "The form works this out — check it matches"),
    portal_prefilled: t("ui.country_return.box.portal_prefilled", "The authority fills this in — check it"),
    portal_input: t("ui.country_return.box.portal_input", "Not on the form — counted in another box if you fill it in"),
    portal_not_on_form: t("ui.country_return.box.portal_not_on_form", "Not on the form — do not copy"),
    kind_typed_required: t("ui.country_return.box.kind_typed_required", "You enter the value — needed before filing"),
    kind_optional: t("ui.country_return.box.kind_optional", "Only if it applies"),
    kind_typed: t("ui.country_return.box.kind_typed", "You enter the value"),
    kind_stub: t("ui.country_return.box.kind_stub", "Not used by this calculation"),
    kind_computed: t("ui.country_return.box.kind_computed", "Worked out by the app"),
  };
  const info = (english || box.computed_from || explainer) ? (
    <InfoCircle testid={`box-info-${box.box_id}`}>
      {english && (
        <div className="font-semibold text-slate-900 dark:text-slate-50 mb-1">{english}</div>
      )}
      {box.computed_from && (
        <div className="mb-1">
          <span className="text-xs text-slate-500 dark:text-slate-400 font-semibold block mb-0.5">
            {t("ui.country_return.box.how_calculated", { defaultValue: "How this is calculated" })}
          </span>
          {box.computed_from}
        </div>
      )}
      {explainer && <div className="text-slate-700 dark:text-slate-200">{explainer}</div>}
    </InfoCircle>
  ) : null;
  return (
    <div
      id={`box-${box.box_id}`}
      data-testid={`box-${box.box_id}`}
      data-highlighted={highlighted ? "true" : "false"}
      data-entry={entry}
      data-portal={portal}
      data-copy={copyFigure ? "true" : "false"}
      className={`rounded-lg p-3 scroll-mt-24 transition-shadow duration-500 ${card} ${
        highlighted ? "ring-4 ring-sky-500/80 shadow-lg shadow-sky-500/30" : ""}`}
    >
      {/* v0.4.48 — operator nav improvement: box-id and value sit
          together on one prominent header row, both in the same
          large monospaced weight so the eye lands on "which box · which
          number" in one glance. Kind badge demoted to a small chip
          on the second row. */}
      <div className="flex items-baseline justify-between gap-3 mb-1.5 pb-1.5 border-b border-slate-200/70">
        {/* v0.6.12 — "Box <ref>" only where the form numbers the box.
            The app's own subtotals and named fields carried engine keys
            here ("Box output_vat_total"); they are titled by their plain
            name instead. country_returns/display_ref.py decides which. */}
        <span
          data-testid={`box-id-${box.box_id}`}
          className={`font-bold text-teal-700 dark:text-teal-300 tracking-tight ${
            boxRef ? "tabular-nums text-xl" : "text-base"
          }`}
        >
          {boxRef ? <>Box {boxRef}{/* i18n-ignore */}</> : title}
          {/* A2-372 — the paper form's line number, so the screen can be
              read line by line against the form or another agent's copy. */}
          {box.form_line ? (
            <span
              data-testid={`box-line-${box.box_id}`}
              className="ml-2 text-xs font-medium text-slate-500 dark:text-slate-400 tabular-nums"
            >
              line {box.form_line}{/* i18n-ignore */}
            </span>
          ) : null}
          {!boxRef && info && <span className="ml-1.5 align-middle inline-flex">{info}</span>}
        </span>
        <span
          data-testid={`box-value-${box.box_id}`}
          className="tabular-nums font-bold text-slate-900 dark:text-slate-50 text-xl tracking-tight"
        >
          {/* v0.6.6 — was parseFloat(box.value), which stops at the first
              non-numeric character instead of failing: "1,234.56" silently
              rendered as "1.00", the wrong VAT figure with no indication
              anything was off. Number() requires the WHOLE string to be
              numeric, so a malformed value renders NaN → the explicit
              fallback below, not a truncated wrong number. */}
          {Number.isFinite(Number(box.value)) && String(box.value ?? "").trim() !== ""
            ? Number(box.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : (box.value ?? "—")}
        </span>
      </div>
      {/* v0.6.12 (A2-112) — when the authority's field takes a different
          unit (Hungary: thousands of forints), say what to TYPE. The value
          above stays in forints; 642,000 is entered as 642. */}
      {entryScale > 1 && entryUnit && Number.isFinite(Number(box.value)) && (
        <div data-testid={`box-entry-${box.box_id}`}
             className="text-xs font-medium text-teal-700 dark:text-teal-300 mb-1 tabular-nums">
          {t("ui.country_return.box.enter_on_form", { value: (Number(box.value) / entryScale).toLocaleString(undefined, { maximumFractionDigits: 0 }), unit: entryUnit, defaultValue: `Enter on the form: ${(Number(box.value) / entryScale).toLocaleString(undefined, { maximumFractionDigits: 0 })} (${entryUnit})` })}
        </div>
      )}
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex-1">
          {boxRef && (
            <div className="flex items-center gap-1.5 mb-1">
              <div data-testid={`box-label-${box.box_id}`} className="text-sm text-slate-900 dark:text-slate-50">{title}</div>
              {info}
            </div>
          )}
          {statusKey && (
            <span data-testid={`box-status-${box.box_id}`} data-status={statusKey}
                  className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border ${
                    STATUS_STYLE[statusKey] || "border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300"}`}>
              {STATUS_TEXT[statusKey]}
            </span>
          )}
        </div>
        {box.kind === "typed_in" && (
          <div className="text-right">
            <TypedInEditor
              box={box} calcId={calcId} country={country} onSaved={onSaved}
              periodStart={periodStart} clientCadence={clientCadence}
              moduleIndex={moduleIndex}
              routeDestinationOffenders={routeDestinationOffenders}
              calcFrozen={calcFrozen}
              calcSignedOff={calcSignedOff}
            />
          </div>
        )}
      </div>
      {note && (
        <div data-testid={`box-note-${box.box_id}`} className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-snug">
          {note}
        </div>
      )}
      {ov && (
        <div
          data-testid={`box-override-stamp-${box.box_id}`}
          className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-700 text-[11px] text-emerald-800 dark:text-emerald-200 leading-snug"
        >
          {/* v0.5.0d+6 — Promoted from a subtle amber footnote
              to a visible emerald "✓ Saved override" pill so that
              after the operator clicks Save and the editor
              closes, the box card carries a positive confirmation
              that the save round-trips on the server. Closes the
              "I press save and can't see anything different on
              the box" perception bug. */}
          <span className="font-bold">✓ {autoFill || t("ui.country_return.box.entered_by_you", { defaultValue: "Value entered by you" })}</span>
          {!autoFill && <>{" · "}{ov.by || t("ui.country_return.box.operator_fallback", { defaultValue: "operator" })}</>}
          {" · "}{String(ov.at || "").slice(0, 16).replace("T", " ")}
        </div>
      )}
      {box.error && (
        <div className="text-xs text-rose-700 dark:text-rose-300 mt-1">⚠ {box.error}</div>
      )}
    </div>
  );
};
